import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getUserInfo } from '../utils/tokenManager';
import { getSupplierPartners, getCurrencyCodes, getHotelProductTypes, addHotelProduct, createSupplierPartner } from '../services/apiService';
import PlacePickerModal from './PlacePickerModal';

const HotelNewProductPlaceModal = ({ open, onClose, onNext, googleApiKey }) => {
  const [mounted, setMounted] = useState(false);
  const [animOut, setAnimOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [partners, setPartners] = useState([]);
  const [partnerFilter, setPartnerFilter] = useState('');
  const [codesLoading, setCodesLoading] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [placeModalOpen, setPlaceModalOpen] = useState(false);
  const [partnerOpen, setPartnerOpen] = useState(false);
  const partnerRef = useRef(null);
  const [newPartnerName, setNewPartnerName] = useState('');
  const [creatingPartner, setCreatingPartner] = useState(false);

  const u = getUserInfo() || {};
  const supplierNo = u.companyNo ? String(u.companyNo) : '';
  const userNo = u.userNo ? String(u.userNo) : '';

  const [form, setForm] = useState({
    hotelName: '',
    supplierNo: supplierNo,
    userNo: userNo,
    placeNo: '',
    placeName: '',
    partnerNo: '',
    baseCurrencyCode: '058001',
    costCurrencyCode: '058001',
    productTypeCode: '',
  });

  useEffect(() => {
    if (open) {
      setMounted(true);
      setAnimOut(false);
      setPartnerFilter('');
      setForm((prev) => ({
        ...prev,
        supplierNo,
        userNo,
      }));
      (async () => {
        try {
          setLoading(true);
          const list = await getSupplierPartners();
          setPartners(Array.isArray(list) ? list : []);
        } catch (e) {
          setPartners([]);
        } finally {
          setLoading(false);
        }
      })();
      (async () => {
        try {
          setCodesLoading(true);
          const [cur, types] = await Promise.all([
            getCurrencyCodes().catch(() => []),
            getHotelProductTypes().catch(() => []),
          ]);
          setCurrencies(Array.isArray(cur) ? cur : []);
          setProductTypes(Array.isArray(types) ? types : []);
          setForm((prev) => ({
            ...prev,
            baseCurrencyCode: prev.baseCurrencyCode || '058001',
            costCurrencyCode: prev.costCurrencyCode || '058001',
            productTypeCode: prev.productTypeCode || (Array.isArray(types) && types[0]?.코드 ? String(types[0].코드) : ''),
          }));
        } finally {
          setCodesLoading(false);
        }
      })();
    }
  }, [open, supplierNo, userNo]);

  // Close partner dropdown on outside click
  useEffect(() => {
    if (!partnerOpen) return;
    const handleDown = (e) => {
      if (partnerRef.current && !partnerRef.current.contains(e.target)) {
        setPartnerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDown);
    return () => document.removeEventListener('mousedown', handleDown);
  }, [partnerOpen]);

  // codes are now loaded on open (see above)

  const closeModal = () => {
    setAnimOut(true);
    setTimeout(() => {
      setMounted(false);
      onClose && onClose();
    }, 180);
  };

  const canSave = useMemo(() => {
    if (!form.hotelName || !form.hotelName.trim()) return false;
    if (!form.supplierNo) return false;
    if (!form.userNo) return false;
    if (!form.placeNo || !String(form.placeNo).trim()) return false;
    if (!form.partnerNo) return false;
    if (!form.baseCurrencyCode) return false;
    if (!form.costCurrencyCode) return false;
    if (!form.productTypeCode) return false;
    return true;
  }, [form]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectPartner = (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, partnerNo: val }));
  };

  const filteredPartners = useMemo(() => {
    const q = (partnerFilter || '').trim().toLowerCase();
    if (!q) return Array.isArray(partners) ? partners : [];
    return (Array.isArray(partners) ? partners : []).filter((p) => {
      const name = String(p?.거래처명 || '').toLowerCase();
      const no = String(p?.거래처번호 || '').toLowerCase();
      return name.includes(q) || no.includes(q);
    });
  }, [partners, partnerFilter]);

  const selectedPartnerName = useMemo(() => {
    const sel = (Array.isArray(partners) ? partners : []).find(
      (p) => String(p?.거래처번호 ?? '') === String(form.partnerNo || '')
    );
    return sel?.거래처명 || '';
  }, [partners, form.partnerNo]);

  const handleCreatePartner = async () => {
    const name = (newPartnerName || '').trim();
    if (!name) return;
    try {
      setCreatingPartner(true);
      await createSupplierPartner({ name });
      const list = await getSupplierPartners();
      setPartners(Array.isArray(list) ? list : []);
      const found = (Array.isArray(list) ? list : []).find(p => String(p?.거래처명 || '') === name);
      if (found && found.거래처번호 != null) {
        setForm(prev => ({ ...prev, partnerNo: String(found.거래처번호) }));
      }
      setNewPartnerName('');
      setPartnerOpen(false);
    } catch (e) {
      alert('거래처 생성에 실패했습니다.');
    } finally {
      setCreatingPartner(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSave) return;
    (async () => {
      try {
        setSaving(true);
        const resp = await addHotelProduct({
          productName: form.hotelName.trim(),
          supplierNo: form.supplierNo,
          userNo: form.userNo,
          placeNo: Number(form.placeNo),
          partnerNo: form.partnerNo,
          baseCurrencyCode: form.baseCurrencyCode,
          costCurrencyCode: form.costCurrencyCode,
          productTypeCode: form.productTypeCode,
        });
        let productNo = null;
        if (resp && Array.isArray(resp.table) && resp.table.length) productNo = resp.table[0].상품번호 || resp.table[0].productNo || resp.table[0].번호;
        if (!productNo && resp && Array.isArray(resp.table1) && resp.table1.length) productNo = resp.table1[0].상품번호 || resp.table1[0].productNo || resp.table1[0].번호;
        if (!productNo) {
          alert('상품번호를 확인하지 못했습니다.');
          return;
        }
        onNext && onNext({ productNo });
        closeModal();
      } catch (err) {
        alert('저장 중 오류가 발생했습니다.');
      } finally {
        setSaving(false);
      }
    })();
  };

  const openPlacePicker = () => {
    setPlaceModalOpen(true);
  };

  const handlePickPlace = ({ placeNo, placeName }) => {
    const pno = placeNo ? String(placeNo) : '';
    const pname = placeName || '';
    setForm((prev) => ({ ...prev, placeNo: pno, placeName: pname, hotelName: prev.hotelName || pname }));
    setPlaceModalOpen(false);
  };

  if (!open && !mounted) return null;

  return createPortal(
    <div className={`fixed inset-0 z-50 ${animOut ? '' : ''}`}>
      <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
      <div className={`absolute inset-x-0 top-[8vh] mx-auto w-full max-w-2xl rounded-xl bg-white shadow-lg border border-gray-200 transition-transform ${animOut ? 'scale-95 opacity-0' : 'scale-100 opacity-100'}`}>
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="text-base font-semibold text-gray-800">호텔 새 상품 등록</div>
          <button type="button" className="p-2 text-gray-500 hover:text-gray-700" onClick={closeModal}>
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">장소</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 text-sm text-gray-800 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2">
                {form.placeNo ? (form.placeName || `선택됨: #${form.placeNo}`) : '미선택'}
              </div>
              <button type="button" className="px-3 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50" onClick={openPlacePicker}>
                장소 선택
              </button>
            </div>
           
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">호텔명</label>
            <input name="hotelName" value={form.hotelName} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500" placeholder="예: 롯데호텔 서울" />
          </div>
          <div ref={partnerRef}>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">공급거래처</label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setPartnerOpen((v) => !v)}
                className="w-full flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
              >
                <span className={selectedPartnerName ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedPartnerName || '공급거래처 선택'}
                </span>
                <svg className={`w-4 h-4 text-gray-500 transition-transform ${partnerOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" clipRule="evenodd"/></svg>
              </button>
              {partnerOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      autoFocus
                      value={partnerFilter}
                      onChange={(e) => setPartnerFilter(e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                      placeholder="거래처명 검색"
                    />
                  </div>
                  <div className="max-h-56 overflow-auto">
                    {loading && (
                      <div className="px-3 py-2 text-[12px] text-gray-500">거래처 불러오는 중...</div>
                    )}
                    {!loading && filteredPartners.length === 0 && (
                      <div className="px-3 py-2 text-[12px] text-gray-500">검색 결과 없음</div>
                    )}
                    {!loading && filteredPartners.map((p) => (
                      <button
                        type="button"
                        key={`${p.거래처번호}-${p.거래처명}`}
                        onClick={() => {
                          setForm((prev) => ({ ...prev, partnerNo: String(p.거래처번호 ?? '') }));
                          setPartnerOpen(false);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${String(form.partnerNo || '') === String(p.거래처번호 ?? '') ? 'bg-blue-50 text-blue-700' : 'text-gray-800'}`}
                      >
                        {p.거래처명}
                      </button>
                    ))}
                  </div>
                  <div className="p-2 border-t border-gray-100 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newPartnerName}
                        onChange={(e) => setNewPartnerName(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
                        placeholder="새 거래처명 입력"
                      />
                      <button
                        type="button"
                        onClick={handleCreatePartner}
                        disabled={creatingPartner || !newPartnerName.trim()}
                        className="px-2.5 py-1.5 text-sm rounded-md bg-primary-600 text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-700"
                      >
                        {creatingPartner ? '추가 중...' : '신규 추가'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">기초통화코드</label>
            <select name="baseCurrencyCode" value={form.baseCurrencyCode} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
              {(currencies || []).map((c) => (
                <option key={String(c.코드)} value={String(c.코드)}>{c.코드명}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">원가통화코드</label>
            <select name="costCurrencyCode" value={form.costCurrencyCode} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
              {(currencies || []).map((c) => (
                <option key={String(c.코드)} value={String(c.코드)}>{c.코드명}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-600 mb-1">상품타입</label>
            <select name="productTypeCode" value={form.productTypeCode} onChange={handleChange} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500">
              <option value="">선택</option>
              {(productTypes || []).map((t) => (
                <option key={String(t.코드)} value={String(t.코드)}>{t.코드명}</option>
              ))}
            </select>
            {codesLoading && <div className="mt-1 text-[12px] text-gray-500">코드 불러오는 중...</div>}
          </div>
          <div className="pt-2 flex items-center justify-end gap-2 border-t border-gray-100 mt-2">
            <button type="button" onClick={closeModal} className="px-4 py-2 text-sm rounded-lg border border-gray-300 hover:bg-gray-50">취소</button>
            <button type="submit" disabled={!canSave || saving} className={`px-4 py-2 text-sm rounded-lg text-white ${!canSave || saving ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'}`}>{saving ? '저장 중...' : '저장'}</button>
          </div>
        </form>
      </div>
      {placeModalOpen && (
        <PlacePickerModal
          open={placeModalOpen}
          onClose={() => setPlaceModalOpen(false)}
          onPick={handlePickPlace}
          googleApiKey={googleApiKey}
        />
      )}
    </div>,
    document.body
  );
};

export default HotelNewProductPlaceModal;
